import { cn, Divider, Stack } from 'component-library';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import {
  MapRadiusContext,
  NO_RADIUS,
  RADIUS_LABEL_MAPPING,
  RadiusType,
  useMapRadiusContext,
} from '@/components/MapOverview/components/MapRadiusContextProvider';
import {
  ComplianceLogo,
  FrameworksLink,
  NCCard,
  NCCard2SectionLayout,
  NCCardHeader,
  SmallScreenExplainerOutlet,
} from '@/components/NCCard/NCCard';
import { InvasiveSpeciesExplainer } from '@/components/NCExplainers/InvasiveSpeciesExplainer';
import { COMPLIANCE_LOGOS_INVASIVE_SPECIES } from '@/config/constants';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlotFacts, usePlotFactsByRadius } from '@/pages/shared/hooks/usePlotFact';
import { getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';

import { useLandscapeViewContext } from '../../LandscapeViewContextProvider';
import { InvasiveSpeciesGraphTile } from './GraphTile';
import { InvasiveSpeciesStatsTile } from './StatsTile';

const FACT_NAMES = {
  graph: IndicatorName.invasive_species_richness,
  speciesId: IndicatorName.invasive_species_species_id,
  registeredSpeciesId: IndicatorName.invasive_species_registered_species_id,
};

export const InvasiveSpecies = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.invasiveSpecies.labels.invasiveSpecies')} errorFallBack={<></>}>
      <InvasiveSpeciesRenderer />
    </PlotCardWrapper>
  );
};

const InvasiveSpeciesRenderer = () => {
  const hasRadiusContext = !!useContext(MapRadiusContext);

  return hasRadiusContext ? <InvasiveSpeciesBaseWithRadius /> : <InvasiveSpeciesBaseWithoutRadius />;
};

const InvasiveSpeciesBaseWithRadius = () => {
  const { isLandscapeActive } = useLandscapeViewContext();
  const { radius } = useMapRadiusContext();

  const facts = usePlotFactsByRadius(Object.values(FACT_NAMES));

  if (!isLandscapeActive) {
    return null;
  }

  return <InvasiveSpeciesBase radius={radius} facts={facts} />;
};

const InvasiveSpeciesBaseWithoutRadius = () => {
  const facts = usePlotFacts(Object.values(FACT_NAMES), undefined, undefined, {
    [PlotReportFactLabelNameEnum.landscape_radius_m]: RADIUS_LABEL_MAPPING[`${NO_RADIUS}`],
  });

  return <InvasiveSpeciesBase facts={facts} />;
};

const InvasiveSpeciesBase = ({
  radius,
  facts,
}: {
  radius?: RadiusType;
  facts?: PlotReportFactElement<unknown>[] | undefined;
}) => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const indicator = GraphTileType.landscape_invasive_species;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const graphData = getFactsFromFactArray(facts, FACT_NAMES.graph) ?? [];
  const speciesIdFact = getFactsFromFactArray(facts, FACT_NAMES.speciesId) ?? [];
  const registeredSpeciesIdFact = getFactsFromFactArray(facts, FACT_NAMES.registeredSpeciesId) ?? [];

  const hasChartData = !!graphData && graphData?.length > 0;
  const hasStatsData =
    (!!speciesIdFact && speciesIdFact?.length > 0) ||
    (!!registeredSpeciesIdFact && registeredSpeciesIdFact?.length > 0);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  const radiusLabel = radius && radius !== NO_RADIUS ? ` ${t('shared.projects.labels.radiusHint', { radius })}` : '';

  return (
    <NCCard explainer={<InvasiveSpeciesExplainer />} data-testid='invasive-species-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.invasiveSpecies.labels.invasiveSpecies') + radiusLabel}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <InvasiveSpeciesGraphTile
          invasiveSpecies={graphData}
          radius={radius}
          activeCustomBenchmark={activeCustomBenchmark}
          selectedBenchmarkId={selectedBenchmarkId}
          onBenchmarkSelect={setSelectedBenchmarkId}
        />
        <InvasiveSpeciesStatsTile speciesIdFact={speciesIdFact} registeredSpeciesIdFact={registeredSpeciesIdFact} />
      </NCCard2SectionLayout>
      <Divider />
      <CustomComplianceFooter />
    </NCCard>
  );
};

const CustomComplianceFooter = () => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';
  const DATA_SOURCES = [
    {
      label: t('shared.ncaDetail.details.invasiveSpecies.sources.gbif'),
      url: 'https://www.gbif.org/',
    },
    {
      label: t('shared.ncaDetail.details.invasiveSpecies.sources.griis'),
      url: 'https://griis.org/',
    },
  ];

  return (
    <>
      <SmallScreenExplainerOutlet />
      <div
        data-testid='compliance-footer'
        className={cn('flex flex-col items-center justify-between gap-3 lg:flex-row px-6 py-4')}
      >
        {!isSmallScreen && (
          <Stack spacing={3} direction='col' data-testid='compliance-footer-stack'>
            <Stack direction='row' spacing={3} className='flex-wrap items-center'>
              <span className='typography-body1 text-text-primary'>
                {t('shared.ncaDetail.details.invasiveSpecies.labels.dataSources')}
              </span>
              {DATA_SOURCES.map((source) => (
                <a
                  key={source.url}
                  href={source.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='underline text-text-primary'
                >
                  {source.label}
                </a>
              ))}
            </Stack>
            <Stack direction='row' spacing={3} className='flex-wrap items-center'>
              <span className='typography-body1 text-text-primary'>
                {t('shared.ncaDetail.details.invasiveSpecies.labels.usefulForReporting')}
              </span>
              {COMPLIANCE_LOGOS_INVASIVE_SPECIES.map((logo) => (
                <ComplianceLogo key={logo} logo={logo} />
              ))}
              <FrameworksLink />
            </Stack>
          </Stack>
        )}
      </div>
    </>
  );
};
